import { ArrowRight, MessageCircle, Phone, Sparkles, Star, BarChart3, Target, Users, TrendingUp, MoveUpRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { trackEvent } from "@/lib/tracking";
import { Reveal, MagneticButton, useMouseParallax } from "@/lib/motion";
import AIBackdrop from "@/components/site/AIBackdrop";

export default function Hero({ content, settings }) {
    const { t, pick } = useI18n();
    const hero = content?.hero || {};
    const badges = pick(hero.badges) || ["Lead Funnels", "Google Ads", "Meta Ads", "WhatsApp", "Tracking"];
    const parallax = useMouseParallax(10);

    const wa = `https://wa.me/${(settings?.whatsapp_number || "4915231656312").replace(/\D/g, "")}?text=${encodeURIComponent(settings?.whatsapp_prefill || "")}`;
    const tel = `tel:${(settings?.phone_number || "+4915231656312").replace(/\s/g, "")}`;

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative isolate overflow-hidden bg-[#0F172A] text-white pt-28 lg:pt-36 pb-24 lg:pb-32 bm-noise"
        >
            {/* AI Mikroanimation — ruhiger Datenfluss im Hintergrund */}
            <AIBackdrop intensity="subtle" className="z-0" />

            {/* glow blobs — subtle mouse parallax */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -left-24 h-[42rem] w-[42rem] bm-glow-emerald bm-glow-emerald-anim opacity-40 bm-parallax-layer"
                style={{ transform: `translate(${parallax.x * 0.6}px, ${parallax.y * 0.6}px)` }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-32 -right-24 h-[36rem] w-[36rem] bm-glow-champagne bm-glow-champagne-anim opacity-50 bm-parallax-layer"
                style={{ transform: `translate(${-parallax.x * 0.4}px, ${-parallax.y * 0.4}px)` }}
            />
            <div aria-hidden className="absolute inset-0 bm-grid-bg opacity-[0.4] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />

            <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                <Reveal as="div" className="lg:col-span-7">
                    <div className="bm-chip mb-6" data-testid="hero-eyebrow">
                        <Sparkles className="size-3.5" />
                        <span>{pick(hero.eyebrow) || t("hero.top")}</span>
                    </div>

                    <h1
                        className="font-display font-bold tracking-[-0.01em] text-[2.25rem] sm:text-[2.75rem] lg:text-[3.25rem] xl:text-[3.75rem] leading-[1.1]"
                        data-testid="hero-headline"
                    >
                        {pick(hero.headline)?.split(". ").map((seg, i) => (
                            <span key={i} className="block">
                                {seg.replace(/\.+$/, "")}.
                            </span>
                        ))}
                    </h1>

                    <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed" data-testid="hero-sub">
                        {pick(hero.subheadline)}
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-trust-row">
                        <div className="flex items-center gap-1.5 text-[#C8A96B]">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} className="size-4 fill-[#C8A96B]" />
                            ))}
                        </div>
                        <span className="text-sm text-white/65">{pick(hero.trust_row) || t("hero.trust")}</span>
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <MagneticButton
                            as="a"
                            href="/#kontakt"
                            onClick={() => trackEvent("cta_click", { position: "hero_primary" })}
                            data-testid="hero-cta-analyse"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#10B981] hover:bg-[#0EA674] text-white font-semibold shadow-[0_18px_45px_-12px_rgba(16,185,129,0.55)] active:scale-[0.98]"
                            strength={14}
                        >
                            {pick(hero.cta_primary) || t("cta.analyse")}
                            <ArrowRight className="size-4" />
                        </MagneticButton>
                        <a
                            href={wa}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackEvent("whatsapp_click", { position: "hero" })}
                            data-testid="hero-cta-whatsapp"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10 transition"
                        >
                            <MessageCircle className="size-4" /> {pick(hero.cta_whatsapp) || t("cta.whatsapp")}
                        </a>
                        <a
                            href={tel}
                            onClick={() => trackEvent("call_click", { position: "hero" })}
                            data-testid="hero-cta-call"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-white/85 hover:text-white font-semibold transition"
                        >
                            <Phone className="size-4" /> {pick(hero.cta_call) || t("cta.call")}
                        </a>
                    </div>

                    <div className="mt-8 flex flex-wrap gap-2" data-testid="hero-badges">
                        {badges.map((b, i) => (
                            <span
                                key={`${b}-${i}`}
                                className="text-xs uppercase tracking-[0.18em] font-semibold text-white/65 px-3 py-1.5 rounded-full bg-white/5 border border-white/10"
                            >
                                ✓ {b}
                            </span>
                        ))}
                    </div>
                </Reveal>

                {/* Right: Premium dashboard mockup */}
                <div className="lg:col-span-5">
                    <Reveal delay={120} className="relative">
                        <div
                            className="absolute -inset-6 bm-glow-emerald opacity-30 -z-10 bm-parallax-layer"
                            style={{ transform: `translate(${parallax.x * 0.3}px, ${parallax.y * 0.3}px)` }}
                            aria-hidden
                        />
                        <div
                            className="bm-parallax-layer"
                            style={{ transform: `translate(${parallax.x * 0.2}px, ${parallax.y * 0.2}px)` }}
                        >
                            <DashboardMockup />
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function DashboardMockup() {
    return (
        <div className="bm-tilt rounded-2xl bm-glass-dark p-5 sm:p-6 text-white border border-white/10 relative overflow-hidden">
            <AIBackdrop intensity="minimal" />
            <div className="relative">
            <div className="flex items-center justify-between text-xs text-white/60">
                <div className="flex items-center gap-1.5">
                    <span className="size-2 rounded-full bg-[#C8A96B]" />
                    <span className="size-2 rounded-full bg-white/40" />
                    <span className="size-2 rounded-full bg-white/20" />
                </div>
                <span className="font-mono uppercase tracking-[0.2em]">cockpit · live</span>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3">
                <Stat label="Leads / 7d" value="+128" trend="+24%" tone="emerald" icon={<Users className="size-4" />} />
                <Stat label="WhatsApp" value="412" trend="+18%" tone="champagne" icon={<MessageCircle className="size-4" />} />
                <Stat label="Conv. Rate" value="8.4%" trend="+1.6 pp" tone="emerald" icon={<TrendingUp className="size-4" />} />
            </div>

            <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-center justify-between text-xs">
                    <span className="text-white/55 uppercase tracking-widest">Pipeline</span>
                    <span className="text-white/65">Letzte 30 Tage</span>
                </div>
                <SparkBars />
                <div className="mt-3 flex items-center gap-2 text-[11px] text-white/60">
                    <BarChart3 className="size-3.5" />
                    Lead Funnel · Meta + Google + WhatsApp
                </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5 flex items-center gap-3">
                    <div className="size-9 rounded-full bg-[#10B981]/20 flex items-center justify-center">
                        <Target className="size-4 text-[#10B981]" />
                    </div>
                    <div>
                        <div className="text-[11px] text-white/55 uppercase tracking-widest">AI Score</div>
                        <div className="font-display font-extrabold text-lg leading-none">87 / 100</div>
                    </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5 flex items-center gap-3">
                    <div className="size-9 rounded-full bg-[#C8A96B]/20 flex items-center justify-center">
                        <MoveUpRight className="size-4 text-[#C8A96B]" />
                    </div>
                    <div>
                        <div className="text-[11px] text-white/55 uppercase tracking-widest">Empfehlung</div>
                        <div className="font-semibold text-sm">Sofort anrufen</div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}

function Stat({ label, value, trend, tone, icon }) {
    const accent = tone === "emerald" ? "text-[#10B981]" : "text-[#C8A96B]";
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-white/55">
                <span>{label}</span>
                <span className={`opacity-80 ${accent}`}>{icon}</span>
            </div>
            <div className="mt-1.5 font-display text-xl font-extrabold leading-none">{value}</div>
            <div className={`mt-1 text-[11px] font-semibold ${accent}`}>{trend}</div>
        </div>
    );
}

function SparkBars() {
    const heights = [22, 30, 24, 38, 32, 44, 40, 52, 48, 58, 50, 64];
    return (
        <div className="mt-3 h-24 flex items-end gap-1.5">
            {heights.map((h, i) => (
                <div
                    key={i}
                    className="flex-1 rounded-t-md"
                    style={{
                        height: `${h}px`,
                        background:
                            i === heights.length - 1
                                ? "linear-gradient(180deg,#10B981, rgba(16,185,129,0.4))"
                                : "linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.05))",
                    }}
                />
            ))}
        </div>
    );
}
